/**
 * @fileoverview Browser geolocation primitives: one-shot fetch, continuous tracking, and
 * permission-change detection.
 * @description The building blocks of location.store.ts, the single source of truth for the
 * device's location. requestCurrentLocation gets the initial fix (and, while permission hasn't been
 * decided, brings up the browser's own permission popup). watchLocation then
 * keeps that fix updated in the background for the rest of the session, so no business request
 * ever waits on a fresh GPS/network read. subscribeToPermissionChange catches the user revoking
 * location from browser settings mid-session, which watchLocation's own error callback won't
 * always reliably surface on its own.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

export interface GeoCoordinates {
  latitude: number
  longitude: number
  accuracy: number
}

export type GeoBlockReason = 'unsupported' | 'denied' | 'unavailable' | 'timeout'

export type LocationResult =
  { ok: true; coordinates: GeoCoordinates } | { ok: false; reason: GeoBlockReason }

/**
 * @description Checks whether the current browser supports the Geolocation API.
 * @returns {boolean} True when geolocation is supported.
 */
export function isGeolocationSupported(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator
}

/**
 * @description Maps a native GeolocationPositionError to this app's reason union.
 * @param {GeolocationPositionError} error - The error the browser reported.
 * @returns {GeoBlockReason} The corresponding reason.
 */
function toBlockReason(error: GeolocationPositionError): GeoBlockReason {
  if (error.code === error.PERMISSION_DENIED) {
    return 'denied'
  }
  if (error.code === error.TIMEOUT) {
    return 'timeout'
  }
  return 'unavailable'
}

/**
 * @description Requests a single current position — the initial fix.
 * @returns {Promise<LocationResult>} The coordinates on success, or the reason it failed.
 */
export function requestCurrentLocation(): Promise<LocationResult> {
  if (!isGeolocationSupported()) {
    return Promise.resolve({ ok: false, reason: 'unsupported' })
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          ok: true,
          coordinates: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          },
        })
      },
      (error) => resolve({ ok: false, reason: toBlockReason(error) }),
      { timeout: 10_000, maximumAge: 0 },
    )
  })
}

/**
 * @description Starts a background subscription that calls onUpdate every time the browser's
 * location provider reports a new fix, for as long as it runs — location.store.ts is the only
 * caller, starting this right after the initial fix succeeds and keeping it alive for the session.
 * @param {(coordinates: GeoCoordinates) => void} onUpdate - Called with each new fix.
 * @param {() => void} onError - Called if the browser reports it can no longer provide location.
 * @returns {number} The watch ID, passed to clearLocationWatch to stop it.
 */
export function watchLocation(
  onUpdate: (coordinates: GeoCoordinates) => void,
  onError: () => void,
): number {
  return navigator.geolocation.watchPosition(
    (position) => {
      onUpdate({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      })
    },
    () => onError(),
    { maximumAge: 30_000 },
  )
}

/**
 * @description Stops a subscription started by watchLocation.
 * @param {number} watchId - The ID returned by watchLocation.
 */
export function clearLocationWatch(watchId: number): void {
  navigator.geolocation.clearWatch(watchId)
}

/**
 * @description Reads the current geolocation permission state, when the browser supports the
 * Permissions API for it (used to re-check after the tab regains focus, since watchLocation can
 * pause in a backgrounded tab).
 * @returns {Promise<PermissionState | null>} The current state, or null when unsupported.
 */
export async function queryGeolocationPermission(): Promise<PermissionState | null> {
  if (!('permissions' in navigator)) {
    return null
  }

  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state
  } catch {
    return null
  }
}

/**
 * @description Subscribes to geolocation permission changes (e.g. the user revoking it from
 * browser settings while the app is open), when the browser supports it.
 * @param {(state: PermissionState) => void} onChange - Called with the new permission state.
 * @returns {() => void} Unsubscribes. A no-op function when the Permissions API isn't supported.
 */
export function subscribeToPermissionChange(
  onChange: (state: PermissionState) => void,
): () => void {
  if (!('permissions' in navigator)) {
    return () => {}
  }

  let status: PermissionStatus | null = null
  let cancelled = false

  const handleChange = () => {
    if (status) {
      onChange(status.state)
    }
  }

  void navigator.permissions.query({ name: 'geolocation' }).then((result) => {
    if (cancelled) {
      return
    }
    status = result
    status.addEventListener('change', handleChange)
  })

  return () => {
    cancelled = true
    status?.removeEventListener('change', handleChange)
  }
}
